import { useEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@mui/material';
import { ChatStreamingText, cn } from '@natter-co/design-system';

import type { Props } from './types';

/**
 * Natalie's read-out: one word every `wordMs`, each resolving out of the DS `ChatStreamingText` blur. The full
 * text reserves the space from the first frame, so nothing below moves while it reads. `instant` starts finished.
 * Screen readers get the whole sentence at once; reduced motion shows it at once.
 *
 * Ported from `natalie/ReadOutText.tsx` in the post-event-feedback prototype.
 */
export const QuestionReadOut = ({
  text,
  wordMs = 90,
  delayMs = 400,
  paused,
  instant,
  onDone,
  id,
  className,
}: Props) => {
  const words = text.split(' ');
  // A JS-driven reveal cannot be stopped by a CSS variant alone.
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
  const [shown, setShown] = useState(reduced || instant ? words.length : 0);
  const done = shown >= words.length;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (reduced) {
      setShown(words.length);
      return undefined;
    }
    if (done || paused) return undefined;
    const timer = window.setTimeout(() => setShown((count) => count + 1), shown === 0 ? delayMs : wordMs);
    return () => window.clearTimeout(timer);
  }, [shown, done, paused, reduced, wordMs, delayMs, words.length]);

  useEffect(() => {
    if (done) onDoneRef.current?.();
  }, [done]);

  return (
    <p id={id} className={cn('m-0 grid text-pretty', className)}>
      <span className="sr-only">{text}</span>
      <span aria-hidden data-testid="question-read-out-reserve" className="invisible col-start-1 row-start-1">
        {text}
      </span>
      <span aria-hidden className="col-start-1 row-start-1">
        <ChatStreamingText
          data-testid="question-read-out-text"
          text={words.slice(0, shown).join(' ')}
          streaming={!done}
        />
      </span>
    </p>
  );
};
